import { collectImportStudyDicomFiles } from './collectImportStudyDicomFiles';
import { checkImportStudyDuplicates } from './checkImportStudyDuplicates';
import { getImportStudyConfig, type ImportStudyConfig } from './importStudyConfig';
import type { ImportStudyPhase } from './importStudyPhases';
import { IMPORT_STUDY_MESSAGES } from './importStudyMessages';
import {
  parseImportStudyDicomFiles,
  type ImportStudyInstanceMetadata,
} from './parseImportStudyDicomFiles';
import {
  validateImportStudyFiles,
  type ImportStudyFile,
  type ImportStudyValidationFailure,
  type ImportStudyValidationResult,
} from './importStudyValidation';

export type ImportStudySelection = {
  dicomFiles: ImportStudyFile[];
  parsedInstances: ImportStudyInstanceMetadata[];
};

export type ImportStudyHandlerSuccess = {
  valid: true;
  dicomFiles: ImportStudyFile[];
  parsedInstances: ImportStudyInstanceMetadata[];
};

export type ImportStudyHandlerResult = ImportStudyHandlerSuccess | ImportStudyValidationFailure;

export type HandleImportStudyFilesOptions = {
  config?: ImportStudyConfig;
  onPhaseChange?: (phase: ImportStudyPhase) => void;
  onValidationError?: (message: string) => void;
  /** Called with extracted DICOM files ready for a future import phase. */
  onDicomFilesReady?: (dicomFiles: ImportStudyFile[]) => void;
  /** Called with parsed metadata ready for viewer consumption. */
  onParsedInstancesReady?: (instances: ImportStudyInstanceMetadata[]) => void;
};

/**
 * Validates, extracts, and parses selected files client-side.
 * No backend upload or persistence is performed.
 */
export async function handleImportStudyFiles(
  files: ImportStudyFile[],
  options: HandleImportStudyFilesOptions = {}
): Promise<ImportStudyHandlerResult> {
  const config = options.config ?? getImportStudyConfig();

  options.onPhaseChange?.('uploading');

  const validationResult: ImportStudyValidationResult = await validateImportStudyFiles(
    files,
    config
  );

  if (!validationResult.valid) {
    if (validationResult.message) {
      options.onValidationError?.(validationResult.message);
    }
    return validationResult;
  }

  options.onPhaseChange?.('extracting');

  const collectionResult = await collectImportStudyDicomFiles(validationResult.files);

  if (!collectionResult.success) {
    if (collectionResult.message) {
      options.onValidationError?.(collectionResult.message);
    }
    return { valid: false, message: collectionResult.message };
  }

  options.onDicomFilesReady?.(collectionResult.dicomFiles);

  options.onPhaseChange?.('reading_dicom');

  const parseResult = await parseImportStudyDicomFiles(
    collectionResult.dicomFiles,
    IMPORT_STUDY_MESSAGES.PARSE_FAILED
  );

  if (!parseResult.success) {
    if (parseResult.message) {
      options.onValidationError?.(parseResult.message);
    }
    return { valid: false, message: parseResult.message };
  }

  const duplicateCheck = checkImportStudyDuplicates(parseResult.instances);

  if (duplicateCheck.hasDuplicates) {
    options.onValidationError?.(IMPORT_STUDY_MESSAGES.DUPLICATE_STUDY);

    if (duplicateCheck.allDuplicates) {
      return { valid: false, message: IMPORT_STUDY_MESSAGES.DUPLICATE_STUDY };
    }
  }

  const parsedInstances = duplicateCheck.hasDuplicates
    ? duplicateCheck.instances
    : parseResult.instances;

  const selection: ImportStudySelection = {
    dicomFiles: collectionResult.dicomFiles,
    parsedInstances,
  };

  options.onParsedInstancesReady?.(selection.parsedInstances);

  if (process.env.NODE_ENV !== 'production') {
    console.info(
      '[ImportStudy] Parsed DICOM instances:',
      selection.parsedInstances.map(instance => ({
        fileName: instance.file.name,
        studyInstanceUID: instance.studyInstanceUID,
        seriesInstanceUID: instance.seriesInstanceUID,
        sopInstanceUID: instance.sopInstanceUID,
        studyDate: instance.studyDate,
        studyDescription: instance.studyDescription,
        modality: instance.modality,
        patientName: instance.patientName,
      }))
    );
  }

  return {
    valid: true,
    dicomFiles: selection.dicomFiles,
    parsedInstances: selection.parsedInstances,
  };
}
